import React, { useCallback, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import proxy from '../../api/axios';
import {
  AdminCard,
  AdminLoading,
  AdminPage,
  AdminPageHeader,
  AdminStatGrid,
  useAdminPageTheme,
} from './adminUi';

const POLL_INTERVAL_MS = 15000;

const AdminAnalytics = () => {
  const { muted } = useAdminPageTheme();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOverview = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await proxy.get('/analytics/overview');
      setOverview(data);
    } catch (error) {
      if (!silent) toast.error('Failed to load analytics');
      console.error(error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
    const interval = setInterval(() => fetchOverview({ silent: true }), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchOverview]);

  const stats = overview
    ? [
        { label: 'Daily Active Users', value: overview.dau },
        { label: `Monthly Active Users (${overview.mauWindowDays}d)`, value: overview.mau },
        { label: 'Total Registered Users', value: overview.totalUsers },
        { label: 'Code Runs', value: overview.totalCodeRuns },
        { label: 'Submissions', value: overview.totalSubmissions },
        { label: 'Concurrent Requests (now)', value: overview.concurrentRequests.current },
        { label: 'Peak Concurrent (since start)', value: overview.concurrentRequests.peakSinceStart },
      ]
    : [];

  return (
    <AdminPage>
      <Helmet>
        <title>Analytics | Admin | CrafterHack</title>
      </Helmet>

      <AdminPageHeader
        title="Analytics"
        subtitle="Live usage metrics, refreshed automatically every 15s"
      />

      {loading ? (
        <AdminLoading />
      ) : (
        <>
          <AdminStatGrid items={stats} />
          <AdminCard>
            <Typography variant="body2" sx={{ color: muted, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RefreshIcon fontSize="inherit" />
              Last updated {overview ? new Date(overview.generatedAt).toLocaleTimeString() : '-'}
            </Typography>
          </AdminCard>
        </>
      )}
    </AdminPage>
  );
};

export default AdminAnalytics;
